import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { motion, AnimatePresence, Reorder, useDragControls } from "motion/react";
import { ACCENT_COLOR } from "../theme";
import WizardInput from "./WizardInput";
import FnrShineButton from "Utilities/FnrShineButton";

const FIELD_TYPES = [
  {
    type: "text",
    label: "Text",
    icon: "fa-font",
    description:
      "Your members can input anything they want in a text box. This is useful for asking something like a profession. If you're expecting longer responses you can have this display as a multi-line text box.",
  },
  {
    type: "number",
    label: "Number",
    icon: "fa-hashtag",
    description:
      "You can ask your members for a number. This is useful for asking something like a class year or number of children. They will only be allowed to enter numeric digits in this field. You can also specify a minimum and maximum number they are allowed to enter.",
  },
  {
    type: "address",
    label: "Address",
    icon: "fa-map-location-dot",
    description: "You can ask your members for an address with this field.",
  },
  {
    type: "date",
    label: "Date",
    icon: "fa-calendar-days",
    description:
      "You can ask your members for any date with this field. These are great for baptism dates, birthdays, or anniversaries.",
  },
  {
    type: "select",
    label: "Select List",
    icon: "fa-list-check",
    description:
      "With this field, you can give your members a pre-made selection to choose an answer from. You can also choose whether to allow your members to select one or multiple responses.",
  },
  {
    type: "file",
    label: "File Upload",
    icon: "fa-file-arrow-up",
    description: "Use this field to request documents from your members.",
  },
];

const ICON_CATEGORIES = [
  {
    label: "Church & Faith",
    icons: [
      "fa-church",
      "fa-cross",
      "fa-book-bible",
      "fa-hands-praying",
      "fa-dove",
      "fa-fish",
      "fa-fire",
      "fa-crown",
      "fa-bookmark",
    ],
  },
  {
    label: "People & Family",
    icons: [
      "fa-user",
      "fa-users",
      "fa-baby",
      "fa-child",
      "fa-person",
      "fa-person-dress",
      "fa-people-group",
      "fa-hand-holding-heart",
      "fa-heart",
      "fa-restroom",
    ],
  },
  {
    label: "Education",
    icons: [
      "fa-graduation-cap",
      "fa-school",
      "fa-book",
      "fa-book-open",
      "fa-chalkboard-user",
      "fa-pencil",
      "fa-pen-ruler",
      "fa-award",
      "fa-certificate",
      "fa-lightbulb",
    ],
  },
  {
    label: "Events & Calendar",
    icons: [
      "fa-calendar",
      "fa-calendar-days",
      "fa-calendar-check",
      "fa-bell",
      "fa-bells",
      "fa-cake-candles",
      "fa-champagne-glasses",
      "fa-rings-wedding",
      "fa-flag",
      "fa-star",
      "fa-music",
      "fa-gift",
      "fa-tree-christmas",
    ],
  },
  {
    label: "Communication",
    icons: [
      "fa-envelope",
      "fa-envelope-open-text",
      "fa-phone",
      "fa-mobile-screen",
      "fa-comment",
      "fa-comment-exclamation",
      "fa-paper-plane",
      "fa-bullhorn",
      "fa-podcast",
      "fa-circle-info",
      "fa-clipboard-check",
      "fa-desktop",
      "fa-laptop",
      "fa-tv",
      "fa-link",
    ],
  },
  {
    label: "Location & Travel",
    icons: [
      "fa-location-dot",
      "fa-map",
      "fa-map-location-dot",
      "fa-map-pin",
      "fa-house",
      "fa-building",
      "fa-city",
      "fa-car",
      "fa-bus",
      "fa-plane",
      "fa-earth-americas",
      "fa-landmark",
    ],
  },
  {
    label: "Forms & Data",
    icons: [
      "fa-font",
      "fa-hashtag",
      "fa-list-check",
      "fa-square-check",
      "fa-file",
      "fa-file-arrow-up",
      "fa-clipboard-list",
      "fa-address-card",
      "fa-copy",
      "fa-calculator",
      "fa-keyboard",
      "fa-sliders",
    ],
  },
  {
    label: "Accessibility",
    icons: [
      "fa-hands-asl-interpreting",
      "fa-eye",
      "fa-eye-low-vision",
      "fa-ear-deaf",
      "fa-wheelchair",
      "fa-universal-access",
      "fa-glasses",
    ],
  },
  {
    label: "Nature & Weather",
    icons: [
      "fa-leaf",
      "fa-tree",
      "fa-seedling",
      "fa-cloud-sun",
      "fa-cloud-rain",
      "fa-rainbow",
      "fa-water",
      "fa-moon",
      "fa-dog",
      "fa-flower-tulip",
      "fa-cloud",
      "fa-sun",
    ],
  },
  {
    label: "Misc",
    icons: [
      "fa-shirt",
      "fa-utensils",
      "fa-mug-hot",
      "fa-pizza-slice",
      "fa-beer-mug-empty",
      "fa-burger",
      "fa-briefcase",
      "fa-palette",
      "fa-camera",
      "fa-lock",
      "fa-venus-mars",
      "fa-gear",
      "fa-rocket",
      "fa-thumbs-up",
      "fa-face-smile-beam",
      "fa-face-grin-tongue",
      "fa-face-frown-open",
      "fa-basketball",
      "fa-image",
      "fa-bag-shopping",
      "fa-signal",
      "fa-tombstone",
    ],
  },
];

function getDefaultIcon(type) {
  const ft = FIELD_TYPES.find((t) => t.type === type);
  return ft ? ft.icon : "fa-font";
}

function getTypeInfo(type) {
  return FIELD_TYPES.find((t) => t.type === type) || FIELD_TYPES[0];
}

/* ── TypePicker ── */

function TypePicker({ onSelect, exitingType }) {
  return (
    <TypeGrid $exiting={!!exitingType}>
      {FIELD_TYPES.map((ft, i) => {
        const isSelected = exitingType === ft.type;
        const isNonSelected = !!exitingType && !isSelected;

        let staggerIdx = 0;
        if (isNonSelected) {
          for (let j = 0; j < i; j++) {
            if (FIELD_TYPES[j].type !== exitingType) staggerIdx++;
          }
        }

        const delay = exitingType
          ? isNonSelected
            ? staggerIdx * 0.08
            : isSelected
              ? 0.5
              : 0
          : 0.15 + i * 0.06;

        return (
          <TypeCard
            key={ft.type}
            as={motion.div}
            onClick={() => !exitingType && onSelect(ft.type)}
            $selected={isSelected}
            initial={{ opacity: 0 }}
            animate={
              exitingType
                ? { opacity: 0, scale: isSelected ? 1.15 : 0.85 }
                : { opacity: 1, scale: 1 }
            }
            transition={{ duration: exitingType ? 0.18 : 0.3, delay }}
          >
            <TypeCardInner>
              <TypeCardIcon className={`fas ${ft.icon}`} />
              <TypeCardLabel>{ft.label}</TypeCardLabel>
            </TypeCardInner>
          </TypeCard>
        );
      })}
    </TypeGrid>
  );
}

/* ── IconGrid ── */

function IconGrid({ selected, onSelect }) {
  return (
    <IconGridWrap>
      {ICON_CATEGORIES.map((cat) => (
        <IconCategory key={cat.label}>
          <IconCategoryLabel>{cat.label}</IconCategoryLabel>
          <IconRow>
            {cat.icons.map((ic) => (
              <IconCell
                key={ic}
                $selected={selected === ic}
                onClick={() => onSelect(ic)}
              >
                <i className={`fas ${ic}`} />
              </IconCell>
            ))}
          </IconRow>
        </IconCategory>
      ))}
    </IconGridWrap>
  );
}

/* ── IconSelect ── */

function IconSelect({ selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const isInitialMount = useRef(true);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    }
    setOpen(!open);
  };

  const handleSelect = (ic) => {
    onSelect(ic);
    setOpen(false);
  };

  return (
    <>
      <IconSelectTrigger ref={triggerRef} onClick={handleOpen} $open={open}>
        <IconSelectPreview>
          <AnimatePresence mode="wait" initial={false}>
            <motion.i
              key={selected}
              className={`fas ${selected}`}
              initial={isInitialMount.current ? false : { opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.15 }}
            />
          </AnimatePresence>
        </IconSelectPreview>
        <IconSelectCaret className={`fas fa-chevron-down`} $open={open} />
      </IconSelectTrigger>
      {createPortal(
        <AnimatePresence>
          {open && (
            <IconSelectDropdown
              ref={dropdownRef}
              as={motion.div}
              initial={{ opacity: 0, scale: 0.92, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -6 }}
              transition={{ duration: 0.15 }}
              style={{
                top: pos.top,
                left: pos.left,
                width: 275,
                transformOrigin: "top left",
              }}
            >
              <IconGrid selected={selected} onSelect={handleSelect} />
            </IconSelectDropdown>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

/* ── SelectOptionsList ── */

function SolChoiceItem({ item, onEdit, onDelete, editIdx, editText, setEditText, onEditSave, onEditCancel, onEditKeyDown }) {
  const dragControls = useDragControls();
  const [dragging, setDragging] = useState(false);
  const [dragDir, setDragDir] = useState(-1);
  const isEditing = editIdx === item.id;

  const handleDragEnd = () => {
    setDragging(false);
    setDragDir(-1);
  };

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      layout="position"
      initial={{ height: 0, opacity: 0, scale: 0.97, paddingBottom: 0 }}
      animate={{ height: "auto", opacity: 1, scale: 1, paddingBottom: 4 }}
      exit={{ height: 0, opacity: 0, scale: 0.95, paddingBottom: 0 }}
      transition={{
        layout: { type: "spring", stiffness: 400, damping: 30 },
        height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
        paddingBottom: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
        scale: { duration: 0.2 },
        opacity: { duration: 0.2 },
      }}
      style={{ listStyle: "none", overflow: "visible", zIndex: dragging ? 10 : 1, position: "relative" }}
      onDragStart={() => { setDragging(true); setDragDir(-1); }}
      onDrag={(e, info) => { if (Math.abs(info.velocity.y) > 5) setDragDir(info.velocity.y > 0 ? 1 : -1); }}
      onDragEnd={handleDragEnd}
    >
      <SolItem $dragging={dragging} $dragDir={dragDir} data-dragging={dragging}>
        {isEditing ? (
          <SolEditInput
            as="textarea"
            rows={1}
            autoFocus
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => onEditKeyDown(e, item.id)}
            onBlur={onEditCancel}
            onFocus={(e) => { const val = e.target.value; e.target.value = ""; e.target.value = val; }}
          />
        ) : (
          <>
            <SolItemText>{item.text}</SolItemText>
            <SolActions>
              <SolActionBtn onClick={() => onEdit(item.id)} title="Edit">
                <i className="fas fa-pencil" />
              </SolActionBtn>
              <SolActionBtn onClick={() => onDelete(item.id)} title="Delete" $danger>
                <i className="fas fa-trash-can" />
              </SolActionBtn>
            </SolActions>
          </>
        )}
        {isEditing ? (
          <SolSaveBtn onMouseDown={(e) => e.preventDefault()} onClick={() => onEditSave(item.id)} disabled={!editText.trim()}>
            <span>Save</span>
          </SolSaveBtn>
        ) : (
          <SolDragHandle onPointerDown={(e) => dragControls.start(e)}>
            <i className="fas fa-grip-vertical" />
          </SolDragHandle>
        )}
      </SolItem>
    </Reorder.Item>
  );
}

function SelectOptionsList({ choices, onChange, onPendingChange, addInputRef }) {
  const [newText, setNewText] = useState("");
  const [editId, setEditId] = useState(-1);
  const [editText, setEditText] = useState("");
  const nextIdRef = useRef(choices.length);
  const addRowRef = useRef(null);
  const localAddInputRef = useRef(null);
  const [items, setItems] = useState(() =>
    choices.map((text, i) => ({ id: i, text })),
  );

  useEffect(() => {
    onPendingChange?.(!!newText.trim() || editId !== -1);
  }, [newText, editId, onPendingChange]);

  // Sync items when choices change externally (e.g. reset)
  const prevChoicesRef = useRef(choices);
  if (choices !== prevChoicesRef.current) {
    prevChoicesRef.current = choices;
    if (choices.length !== items.length || choices.some((c, i) => items[i]?.text !== c)) {
      const synced = choices.map((text, i) => ({ id: nextIdRef.current + i, text }));
      nextIdRef.current += choices.length;
      setItems(synced);
    }
  }

  const pushChoices = (newItems) => {
    onChange(newItems.map((it) => it.text));
  };

  const handleReorder = (newItems) => {
    setItems(newItems);
    pushChoices(newItems);
  };

  const handleAdd = () => {
    const trimmed = newText.trim();
    if (!trimmed) return;
    const id = nextIdRef.current++;
    const newItems = [...items, { id, text: trimmed }];
    setItems(newItems);
    pushChoices(newItems);
    setNewText("");
    setTimeout(() => {
      addRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 250);
  };

  const handleAddKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = (id) => {
    const newItems = items.filter((it) => it.id !== id);
    setItems(newItems);
    pushChoices(newItems);
    if (editId === id) setEditId(-1);
  };

  const handleEditStart = (id) => {
    const item = items.find((it) => it.id === id);
    if (!item) return;
    setEditId(id);
    setEditText(item.text);
  };

  const handleEditCancel = () => {
    setEditId(-1);
  };

  const handleEditSave = (id) => {
    const trimmed = editText.trim();
    if (!trimmed) {
      handleEditCancel();
      return;
    }
    const newItems = items.map((it) => (it.id === id ? { ...it, text: trimmed } : it));
    setItems(newItems);
    pushChoices(newItems);
    setEditId(-1);
  };

  const handleEditKeyDown = (e, id) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleEditSave(id);
    }
    if (e.key === "Escape") handleEditCancel();
  };

  return (
    <SolWrap>
      <Reorder.Group
        axis="y"
        values={items}
        onReorder={handleReorder}
        as="div"
        style={{ display: "flex", flexDirection: "column", padding: 0 }}
      >
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <SolChoiceItem
              key={item.id}
              item={item}
              onEdit={handleEditStart}
              onDelete={handleDelete}
              editIdx={editId}
              editText={editText}
              setEditText={setEditText}
              onEditSave={handleEditSave}
              onEditCancel={handleEditCancel}
              onEditKeyDown={handleEditKeyDown}
            />
          ))}
        </AnimatePresence>
      </Reorder.Group>
      <SolAddRow ref={addRowRef}>
        <SolAddInput
          ref={(el) => {
            localAddInputRef.current = el;
            if (addInputRef) addInputRef.current = el;
          }}
          as="textarea"
          rows={1}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={handleAddKeyDown}
          placeholder="Type an option…"
        />
        <SolAddBtn onMouseDown={(e) => e.preventDefault()} onClick={handleAdd} disabled={!newText.trim()}>
          <span>Add</span>
        </SolAddBtn>
      </SolAddRow>
    </SolWrap>
  );
}

/* ── FieldConfigurator ── */

function FieldConfigurator({
  type,
  label,
  icon,
  options,
  onChangeLabel,
  onChangeIcon,
  onChangeOptions,
  onSave,
  onBack,
  onCancel,
  isEditing,
  exitingBack,
}) {
  const info = getTypeInfo(type);
  const currentIcon = icon || getDefaultIcon(type);
  const [hasPendingChoice, setHasPendingChoice] = useState(false);
  const [minMaxBlurred, setMinMaxBlurred] = useState(false);
  const addOptionInputRef = useRef(null);
  const minVal = options.min === "" || options.min == null ? null : Number(options.min);
  const maxVal = options.max === "" || options.max == null ? null : Number(options.max);
  const minMaxInvalid = minVal !== null && maxVal !== null && minVal > maxVal;
  const hasMinMaxError = minMaxInvalid && minMaxBlurred;
  const canSave =
    label.trim() &&
    (type !== "select" || (options.choices && options.choices.length > 0)) &&
    !hasPendingChoice &&
    !minMaxInvalid;
  const multiline = options.multiline || false;
  const includeYear = options.includeYear !== false;

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter" && !isEditing) {
      e.preventDefault();
      if (type === "select" && (!options.choices || options.choices.length === 0)) {
        addOptionInputRef.current?.focus();
      } else if (canSave) {
        onSave();
      }
    }
  };

  return (
    <ConfigWrap>
      <ConfigBody>
        <ConfigRow
          as={motion.div}
          initial={isEditing ? false : { opacity: 0, y: 20 }}
          animate={exitingBack ? { opacity: 0, y: -15 } : { opacity: 1, y: 0 }}
          transition={
            exitingBack
              ? { duration: 0.18, delay: 0.08 }
              : { duration: 0.28, delay: isEditing ? 0 : 0.67 }
          }
        >
          <IconSelectField>
            <IconSelectFieldLabel>Icon</IconSelectFieldLabel>
            <IconSelect selected={currentIcon} onSelect={onChangeIcon} />
          </IconSelectField>
          <ConfigFieldFlex>
            <WizardInput
              label="Title"
              value={label}
              onChange={onChangeLabel}
              onKeyDown={handleTitleKeyDown}
              placeholder={info.label}
              autoFocus
            />
          </ConfigFieldFlex>
        </ConfigRow>

        {type === "text" && (
          <OptionCard
            as={motion.div}
            initial={isEditing ? false : { opacity: 0, y: 20 }}
            animate={
              exitingBack ? { opacity: 0, y: -15 } : { opacity: 1, y: 0 }
            }
            transition={
              exitingBack
                ? { duration: 0.18, delay: 0.11 }
                : { duration: 0.28, delay: isEditing ? 0 : 0.77 }
            }
          >
            <OptionCardIcon className="fas fa-align-left" />
            <OptionCardBody>
              <OptionCardLabel>
                How many lines should the text box have?
              </OptionCardLabel>
              <OptionCardChoices>
                <RadioChoice
                  $active={!multiline}
                  onClick={() =>
                    onChangeOptions({ ...options, multiline: false })
                  }
                >
                  <RadioDot $active={!multiline} />
                  Single
                </RadioChoice>
                <RadioChoice
                  $active={multiline}
                  onClick={() =>
                    onChangeOptions({ ...options, multiline: true })
                  }
                >
                  <RadioDot $active={multiline} />
                  Multiple
                </RadioChoice>
              </OptionCardChoices>
            </OptionCardBody>
          </OptionCard>
        )}

        {type === "number" && (
          <MinMaxOuter
            as={motion.div}
            initial={isEditing ? false : { opacity: 0, y: 20 }}
            animate={
              exitingBack ? { opacity: 0, y: -15 } : { opacity: 1, y: 0 }
            }
            transition={
              exitingBack
                ? { duration: 0.18, delay: 0.11 }
                : { duration: 0.28, delay: isEditing ? 0 : 0.77 }
            }
          >
            <OptionCard $error={hasMinMaxError}>
              <OptionCardIcon className="fas fa-calculator" />
              <OptionCardBody>
                <MinMaxRow>
                  <SmallInputWrap>
                    <WizardInput
                      label="Minimum"
                      type="number"
                      value={options.min ?? ""}
                      onChange={(v) => {
                        const n = v === "" ? "" : Math.max(0, Number(v));
                        const newMin = n === "" ? "" : String(n);
                        onChangeOptions({ ...options, min: newMin });
                        const nv = newMin === "" ? null : Number(newMin);
                        if (!(nv !== null && maxVal !== null && nv > maxVal)) setMinMaxBlurred(false);
                      }}
                      onBlur={() => { if (minMaxInvalid) setMinMaxBlurred(true); }}
                      placeholder="—"
                    />
                  </SmallInputWrap>
                  <MinMaxDash>–</MinMaxDash>
                  <SmallInputWrap>
                    <WizardInput
                      label="Maximum"
                      type="number"
                      value={options.max ?? ""}
                      onChange={(v) => {
                        const n = v === "" ? "" : Math.max(0, Number(v));
                        const newMax = n === "" ? "" : String(n);
                        onChangeOptions({ ...options, max: newMax });
                        const nv = newMax === "" ? null : Number(newMax);
                        if (!(minVal !== null && nv !== null && minVal > nv)) setMinMaxBlurred(false);
                      }}
                      onBlur={() => { if (minMaxInvalid) setMinMaxBlurred(true); }}
                      placeholder="—"
                    />
                  </SmallInputWrap>
                </MinMaxRow>
              </OptionCardBody>
              <OptionalTag>Optional</OptionalTag>
            </OptionCard>
            <AnimatePresence>
              {hasMinMaxError && (
                <motion.div
                  key="minmax-warn"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: "hidden" }}
                >
                  <MinMaxError>
                    Minimum cannot be greater than maximum
                  </MinMaxError>
                </motion.div>
              )}
            </AnimatePresence>
          </MinMaxOuter>
        )}

        {type === "select" && (
          <OptionCard
            as={motion.div}
            initial={isEditing ? false : { opacity: 0, y: 20 }}
            animate={
              exitingBack ? { opacity: 0, y: -15 } : { opacity: 1, y: 0 }
            }
            transition={
              exitingBack
                ? { duration: 0.18, delay: 0.11 }
                : { duration: 0.28, delay: isEditing ? 0 : 0.77 }
            }
            style={{ alignItems: 'flex-start' }}
          >
            <OptionCardIcon className="fas fa-list" style={{ height: 74, display: 'flex', alignItems: 'center', position: 'sticky', top: 8 }} />
            <OptionCardBody style={{ flex: 1, minWidth: 0 }}>
              <OptionCardLabel>Build your list of choices</OptionCardLabel>
              <SelectOptionsList
                choices={options.choices || []}
                onChange={(choices) =>
                  onChangeOptions({ ...options, choices })
                }
                onPendingChange={setHasPendingChoice}
                addInputRef={addOptionInputRef}
              />
            </OptionCardBody>
          </OptionCard>
        )}

        {type === "select" && (
          <OptionCard
            as={motion.div}
            initial={isEditing ? false : { opacity: 0, y: 20 }}
            animate={
              exitingBack ? { opacity: 0, y: -15 } : { opacity: 1, y: 0 }
            }
            transition={
              exitingBack
                ? { duration: 0.18, delay: 0.14 }
                : { duration: 0.28, delay: isEditing ? 0 : 0.87 }
            }
          >
            <OptionCardIcon className="fas fa-check-double" />
            <OptionCardBody>
              <OptionCardLabel>
                Would you like to allow members to select more than one option?
              </OptionCardLabel>
              <OptionCardChoices>
                <RadioChoice
                  $active={options.multiSelect === true}
                  onClick={() =>
                    onChangeOptions({ ...options, multiSelect: true })
                  }
                >
                  <RadioDot $active={options.multiSelect === true} />
                  Yes
                </RadioChoice>
                <RadioChoice
                  $active={options.multiSelect !== true}
                  onClick={() =>
                    onChangeOptions({ ...options, multiSelect: false })
                  }
                >
                  <RadioDot $active={options.multiSelect !== true} />
                  No
                </RadioChoice>
              </OptionCardChoices>
            </OptionCardBody>
          </OptionCard>
        )}

        {type === "date" && (
          <OptionCard
            as={motion.div}
            initial={isEditing ? false : { opacity: 0, y: 20 }}
            animate={
              exitingBack ? { opacity: 0, y: -15 } : { opacity: 1, y: 0 }
            }
            transition={
              exitingBack
                ? { duration: 0.18, delay: 0.11 }
                : { duration: 0.28, delay: isEditing ? 0 : 0.77 }
            }
          >
            <OptionCardIcon className="far fa-calendar" />
            <OptionCardBody>
              <OptionCardLabel>
                Would you like to include the year?
              </OptionCardLabel>
              <OptionCardChoices>
                <RadioChoice
                  $active={includeYear}
                  onClick={() =>
                    onChangeOptions({ ...options, includeYear: true })
                  }
                >
                  <RadioDot $active={includeYear} />
                  Yes
                </RadioChoice>
                <RadioChoice
                  $active={!includeYear}
                  onClick={() =>
                    onChangeOptions({ ...options, includeYear: false })
                  }
                >
                  <RadioDot $active={!includeYear} />
                  No
                </RadioChoice>
              </OptionCardChoices>
            </OptionCardBody>
          </OptionCard>
        )}

        <ConfigFooter
          as={motion.div}
          initial={isEditing ? false : { opacity: 0 }}
          animate={exitingBack ? { opacity: 0 } : { opacity: 1 }}
          transition={
            exitingBack
              ? { duration: 0.12, delay: 0.14 }
              : { duration: 0.25, delay: isEditing ? 0 : 0.83 }
          }
        >
          <FooterLeft>
            {isEditing ? (
              <BackBtn onClick={onCancel}>
                <span>Cancel</span>
              </BackBtn>
            ) : (
              <BackBtn onClick={onBack}>
                <span>
                  <BackIcon className="fas fa-circle-chevron-left" /> Back
                </span>
              </BackBtn>
            )}
          </FooterLeft>
          <SmallButton onClick={onSave} disabled={!canSave}>
            Save <i className="fas fa-circle-check" />
          </SmallButton>
        </ConfigFooter>
      </ConfigBody>
    </ConfigWrap>
  );
}

/* ── Main Editor ── */

export default function CustomFieldEditor({
  open,
  onClose,
  onSave,
  editingField,
}) {
  const isEditing = !!editingField;
  const [phase, setPhase] = useState(isEditing ? "configure" : "pick");
  const [selectedType, setSelectedType] = useState(
    editingField?.type || "text",
  );
  const [label, setLabel] = useState(editingField?.label || "");
  const [icon, setIcon] = useState(editingField?.icon || "");
  const [options, setOptions] = useState(editingField?.options || {});
  const [direction, setDirection] = useState(1);
  const [exitingType, setExitingType] = useState(null);
  const [exitingBack, setExitingBack] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);
  const panelRef = useRef(null);
  const prevHeightRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (editingField) {
        setPhase("configure");
        setSelectedType(editingField.type || "text");
        setLabel(editingField.label || "");
        setIcon(
          editingField.icon || getDefaultIcon(editingField.type || "text"),
        );
        setOptions(editingField.options || {});
      } else {
        setPhase("pick");
        setSelectedType("text");
        setLabel("");
        setIcon("");
        setOptions({});
      }
      setDirection(1);
      setExitingType(null);
      setSessionKey((k) => k + 1);
    }
  }, [open, editingField]);

  useLayoutEffect(() => {
    const el = panelRef.current;
    const prevH = prevHeightRef.current;
    if (prevH === null || !el) return;
    prevHeightRef.current = null;

    const newH = el.offsetHeight;
    if (newH === prevH) return;

    el.style.height = prevH + "px";
    void el.offsetHeight;
    el.style.transition = "height 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
    el.style.height = newH + "px";

    const onEnd = (e) => {
      if (e.propertyName !== "height") return;
      el.style.height = "";
      el.style.transition = "";
    };
    el.addEventListener("transitionend", onEnd, { once: true });

    return () => {
      el.removeEventListener("transitionend", onEnd);
      el.style.height = "";
      el.style.transition = "";
    };
  }, [phase]);

  const handleTypeSelect = (type) => {
    setExitingType(type);

    // Switch phase while exit animations are still running
    setTimeout(() => {
      if (panelRef.current) {
        prevHeightRef.current = panelRef.current.offsetHeight;
      }
      setSelectedType(type);
      setIcon(getDefaultIcon(type));
      setLabel("");
      setOptions({});
      setDirection(1);
      setPhase("configure");
      // exitingType stays set — phase 1 keeps rendering
    }, 250);

    // Clear exitingType after exit animations finish
    setTimeout(() => {
      setExitingType(null);
    }, 700);
  };

  const handleBack = () => {
    setExitingBack(true);

    setTimeout(() => {
      if (panelRef.current) {
        prevHeightRef.current = panelRef.current.offsetHeight;
      }
      setDirection(-1);
      setPhase("pick");
    }, 250);

    setTimeout(() => {
      setExitingBack(false);
    }, 600);
  };

  const handleSave = () => {
    if (!label.trim()) return;
    if (selectedType === "select" && !(options.choices && options.choices.length > 0)) return;
    onSave({
      type: selectedType,
      label: label.trim(),
      icon: icon || getDefaultIcon(selectedType),
      options,
    });
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <Backdrop
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Panel
            ref={panelRef}
            as={motion.div}
            initial={{ opacity: 0, scale: 0.92, rotateX: -14, y: 40 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0, y: 0 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{
              type: "spring",
              stiffness: 150,
              damping: 15,
              mass: 1,
              opacity: { duration: 0.4 },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseBtn onClick={onClose}>
              <i className="fas fa-circle-xmark" />
            </CloseBtn>
            <PanelContent>
              {(phase === "pick" || exitingType) && (
                <SlideWrap
                  style={
                    phase === "configure"
                      ? {
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          zIndex: 1,
                          pointerEvents: "none",
                        }
                      : undefined
                  }
                >
                  <PanelHeader>
                    <PanelHeaderText>
                      <PanelTitle
                        as={motion.h2}
                        className="merri"
                        initial={direction === -1 ? { opacity: 0 } : false}
                        animate={
                          exitingType
                            ? { opacity: 0, y: -20 }
                            : { opacity: 1, y: 0 }
                        }
                        transition={{ duration: exitingType ? 0.22 : 0.3 }}
                      >
                        {isEditing ? "Edit Custom Field" : "Add a Custom Field"}
                      </PanelTitle>
                      <PanelSubtitle
                        as={motion.p}
                        className="raleway"
                        initial={direction === -1 ? { opacity: 0 } : false}
                        animate={
                          exitingType
                            ? { opacity: 0, y: -15 }
                            : { opacity: 1, y: 0 }
                        }
                        transition={{
                          duration: exitingType ? 0.22 : 0.3,
                          delay: exitingType ? 0.06 : 0.06,
                        }}
                      >
                        Which type of field would you like to create?
                      </PanelSubtitle>
                    </PanelHeaderText>
                  </PanelHeader>
                  <TypePicker
                    onSelect={handleTypeSelect}
                    exitingType={exitingType}
                  />
                </SlideWrap>
              )}
              {(phase === "configure" || exitingBack) && (
                <SlideWrap
                  style={
                    phase !== "configure"
                      ? {
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          zIndex: 1,
                          pointerEvents: "none",
                        }
                      : undefined
                  }
                >
                  <ConfigTitleBar
                    as={motion.div}
                    initial={
                      isEditing
                        ? false
                        : {
                            backgroundColor: "rgba(255,255,255,0)",
                            borderBottomColor: "rgba(229,231,235,0)",
                          }
                    }
                    animate={
                      exitingBack
                        ? {
                            backgroundColor: "rgba(255,255,255,0)",
                            borderBottomColor: "rgba(229,231,235,0)",
                          }
                        : {
                            backgroundColor: "rgba(255,255,255,1)",
                            borderBottomColor: "rgba(229,231,235,1)",
                          }
                    }
                    transition={
                      exitingBack
                        ? { duration: 0.25 }
                        : { duration: 0.3, delay: 0.35 }
                    }
                  >
                    <ConfigTitleIcon
                      as={motion.div}
                      initial={isEditing ? false : { opacity: 0, scale: 0 }}
                      animate={
                        exitingBack
                          ? { opacity: 0, scale: 0 }
                          : { opacity: 1, scale: 1 }
                      }
                      transition={
                        exitingBack
                          ? { duration: 0.15 }
                          : {
                              type: "spring",
                              stiffness: 400,
                              damping: 15,
                              delay: 0.57,
                            }
                      }
                    >
                      <i className={`fas ${getTypeInfo(selectedType).icon}`} />
                    </ConfigTitleIcon>
                    <ConfigTitleText>
                      <PanelTitle
                        as={motion.h2}
                        className="merri"
                        initial={isEditing ? false : { opacity: 0, y: 15 }}
                        animate={
                          exitingBack
                            ? { opacity: 0, y: -15 }
                            : { opacity: 1, y: 0 }
                        }
                        transition={
                          exitingBack
                            ? { duration: 0.2, delay: 0.02 }
                            : { duration: 0.28, delay: isEditing ? 0 : 0.41 }
                        }
                      >
                        {getTypeInfo(selectedType).label}
                      </PanelTitle>
                      <ConfigTitleDesc
                        as={motion.p}
                        className="raleway"
                        initial={isEditing ? false : { opacity: 0, y: 12 }}
                        animate={
                          exitingBack
                            ? { opacity: 0, y: -12 }
                            : { opacity: 1, y: 0 }
                        }
                        transition={
                          exitingBack
                            ? { duration: 0.2, delay: 0.05 }
                            : { duration: 0.28, delay: isEditing ? 0 : 0.49 }
                        }
                      >
                        {getTypeInfo(selectedType).description}
                      </ConfigTitleDesc>
                    </ConfigTitleText>
                  </ConfigTitleBar>
                  <FieldConfigurator
                    key={sessionKey}
                    type={selectedType}
                    label={label}
                    icon={icon}
                    options={options}
                    onChangeLabel={setLabel}
                    onChangeIcon={setIcon}
                    onChangeOptions={setOptions}
                    onSave={handleSave}
                    onBack={handleBack}
                    onCancel={onClose}
                    isEditing={isEditing}
                    exitingBack={exitingBack}
                  />
                </SlideWrap>
              )}
            </PanelContent>
          </Panel>
        </Backdrop>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* ── Styled Components ── */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1200px;
`;

const Panel = styled.div`
  position: relative;
  width: 560px;
  max-height: 80vh;
  background: linear-gradient(135deg, #f9f9fb 0%, #f0f0f3 100%);
  border-radius: 32px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.35),
    0 8px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  position: absolute;
  top: 14px;
  right: 12px;
  padding: 0;
  cursor: pointer;
  color: #c4c4c4;
  font-size: 32px;
  line-height: 1;
  z-index: 10;
  transition:
    color 0.2s,
    transform 0.15s;

  &:hover {
    color: #888;
  }

  &:active {
    transform: scale(0.9);
    color: #666;
  }
`;

const PanelHeader = styled.div`
  padding: 32px 48px 0 48px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PanelHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const ConfigTitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 48px;
  background-color: #fff;
  border-bottom: 1px solid #e5e7eb;
`;

const ConfigTitleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const ConfigTitleDesc = styled.p`
  margin: 0;
  font-size: 16px;
  color: #6b7280;
  line-height: 1.25;
`;

const ConfigTitleIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${ACCENT_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 24px;
    color: #fff;
  }
`;

const PanelHeaderIcon = styled.i`
  font-size: 28px;
  color: ${ACCENT_COLOR};
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 28px;
  color: #182941;
`;

const PanelSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 18px;
  color: #6b7280;
`;

const PanelDesc = styled.p`
  margin: 4px 0 0 0;
  font-size: 16px;
  color: #6b7280;
  line-height: 1.3;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const SlideWrap = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: calc(80vh - 140px);
`;

/* ── TypePicker styles ── */

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 32px;
  ${(p) => p.$exiting && `pointer-events: none;`}
`;

const TypeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px 14px 12px;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  gap: 6px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    background 0.15s,
    transform 0.15s;
  user-select: none;

  &:hover {
    border-color: ${ACCENT_COLOR};
    box-shadow: 0 2px 10px rgba(1, 96, 153, 0.12);
    background: rgba(1, 96, 153, 0.02);
  }

  &:active {
    background: rgba(1, 96, 153, 0.08);
  }

  &:active ${() => TypeCardInner} {
    transform: scale(0.88);
  }

  ${(p) =>
    p.$selected &&
    `
    border-color: ${ACCENT_COLOR};
    background: ${ACCENT_COLOR};
    pointer-events: none;
  `}

  ${(p) =>
    p.$selected &&
    `
    ${TypeCardIcon}, ${TypeCardLabel} {
      color: #fff;
    }
  `}
`;

const TypeCardInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  transition: transform 0.15s;
`;

const TypeCardIcon = styled.i`
  font-size: 28px;
  color: ${ACCENT_COLOR};
`;

const TypeCardLabel = styled.div`
  font-size: 16px;
  color: #182941;
`;

const TypeCardDesc = styled.div`
  font-size: 11.5px;
  color: #9ca3af;
  line-height: 1.35;
`;

/* ── FieldConfigurator styles ── */

const ConfigWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const ConfigHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 32px;
  border-bottom: 1px solid #f0f0f0;
`;

const ConfigTypeIcon = styled.i`
  font-size: 26px;
  color: ${ACCENT_COLOR};
  flex-shrink: 0;
`;

const ConfigHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ConfigTypeName = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #182941;
`;

const ConfigTypeDesc = styled.p`
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
`;

const OptionCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 20px;
  background: #fff;
  border: 1px solid ${(p) => (p.$error ? "#f59e0b" : "#d1d5db")};
  border-radius: ${(p) => (p.$error ? "10px 10px 0 0" : "10px")};
  transition: border-color 0.2s;
`;

const MinMaxOuter = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionCardIcon = styled.i`
  font-size: 28px;
  color: ${ACCENT_COLOR};
  flex-shrink: 0;
  margin-left: 4px;
  margin-right: 19px;
`;

const OptionCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OptionCardLabel = styled.div`
  font-size: 17px;
  color: #374151;
`;

const OptionCardChoices = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OptionalTag = styled.div`
  position: absolute;
  top: 10px;
  right: 11px;
  font-size: 11px;
  font-weight: 700;
  color: ${ACCENT_COLOR};
  background: rgba(1, 96, 153, 0.08);
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const MinMaxRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
`;

const SmallInputWrap = styled.div`
  width: 100px;

  > div {
    gap: 4px;
  }

  label {
    font-size: 14px;
    padding-left: 12px;
  }

  input {
    padding: 8px 12px;
    font-size: 16px;
    border-radius: 8px;
  }
`;

const MinMaxDash = styled.span`
  font-size: 18px;
  color: #9ca3af;
  margin-bottom: 8px;
`;

const MinMaxError = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 0 6px;
  background: #fef9e7;
  border: 1px solid #f59e0b;
  border-top: none;
  border-radius: 0 0 10px 10px;
  color: #b45309;
  font-size: 13px;
  white-space: nowrap;

  i {
    color: #f59e0b;
    font-size: 11px;
    flex-shrink: 0;
  }
`;

const RadioChoice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 17px;
  color: ${(p) => (p.$active ? "#182941" : "#6b7280")};
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
`;

const RadioDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$active ? ACCENT_COLOR : "#d1d5db")};
  background: ${(p) => (p.$active ? ACCENT_COLOR : "transparent")};
  position: relative;
  transition:
    border-color 0.15s,
    background 0.15s;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fff;
    opacity: ${(p) => (p.$active ? 1 : 0)};
    transition: opacity 0.15s;
  }
`;

const ConfigBody = styled.div`
  padding: 24px 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
`;

const ConfigRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const ConfigFieldFlex = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const IconSelectField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const IconSelectFieldLabel = styled.label`
  font-size: 16px;
  color: #4b5563;
  padding-left: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ConfigFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
`;

const BackBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-size: 15px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px 10px 8px 8px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
  transition:
    background 0.2s,
    color 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #016099;
  }

  > span {
    display: flex;
    align-items: center;
    gap: 4px;
    transition: transform 0.2s;
  }

  &:active {
    background: #016099;
    color: #fff;

    > span {
      transform: scale(0.85);
    }
  }
`;

const BackIcon = styled.i`
  font-size: 1em;
  margin-right: 2px;
`;

function SmallButton({ onClick, disabled, children }) {
  return (
    <SmallBtnWrap $disabled={disabled}>
      <FnrShineButton onClick={onClick} disabled={disabled} borderRadius={100}>
        <SmallBtnInside>{children}</SmallBtnInside>
      </FnrShineButton>
    </SmallBtnWrap>
  );
}

const SmallBtnWrap = styled.div`
  background: ${(p) => (p.$disabled ? "#d1d5db" : ACCENT_COLOR)};
  border-radius: 100px;
  transition: background 0.2s;
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
`;

const SmallBtnInside = styled.div`
  font-size: 20px;
  color: #fff;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 3px;
  padding: 10px 10px 10px 22px;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 24px;
  }
`;

/* ── IconGrid styles ── */

const IconGridWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const IconCategory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const IconCategoryLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const IconRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const IconCell = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  color: ${(p) => (p.$selected ? "#fff" : "#6b7280")};
  background: ${(p) => (p.$selected ? ACCENT_COLOR : "rgba(0,0,0,0.04)")};
  border: 1.5px solid ${(p) => (p.$selected ? ACCENT_COLOR : "transparent")};
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s,
    transform 0.1s;

  &:hover {
    background: ${(p) => (p.$selected ? ACCENT_COLOR : "rgba(0,0,0,0.08)")};
    color: ${(p) => (p.$selected ? "#fff" : "#374151")};
  }

  i {
    transition: transform 0.1s;
  }

  &:active i {
    transform: scale(0.8);
  }
`;

/* ── IconSelect styles ── */

const IconSelectTrigger = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  cursor: pointer;
  background: #fff;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;

  ${(p) =>
    p.$open &&
    `
    box-shadow: 0 0 4px 2px ${ACCENT_COLOR};
  `}

  &:hover {
    box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.15);
  }

  &:active {
    box-shadow: 0 0 4px 2px ${ACCENT_COLOR};
  }

  &:active ${() => IconSelectPreview} {
    transform: scale(0.88);
  }

  ${(p) =>
    p.$open &&
    `
    &:hover {
      box-shadow: 0 0 4px 2px ${ACCENT_COLOR};
    }
  `}
`;

const IconSelectPreview = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 4px;
  background: rgba(1, 96, 153, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  transition: transform 0.15s;

  i {
    font-size: 28px;
    color: ${ACCENT_COLOR};
  }
`;

const IconSelectCaret = styled.i`
  font-size: 12px;
  color: #9ca3af;
  transition: transform 0.2s;
  ${(p) => p.$open && `transform: rotate(180deg);`}
`;

const IconSelectDropdown = styled.div`
  position: fixed;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  padding: 10px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 300;
`;

/* ── SelectOptionsList styles ── */

const SolWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SolAddRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 6px 6px 18px;
  background: #f5f5f6;
  border-radius: 10px;
  position: relative;
  z-index: 2;
  scroll-margin-bottom: 50px;
  transition: box-shadow 0.15s;

  &:focus-within {
    box-shadow: 0 0 4px 2px ${ACCENT_COLOR};
  }
`;

const SolAddInput = styled.input`
  flex: 1;
  padding: 0;
  font-size: 15px;
  font-family: inherit;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  overflow: hidden;
  line-height: 1.4;
  field-sizing: content;
  align-self: center;

  &::placeholder {
    color: #b0b0b0;
  }
`;

const SolAddBtn = styled.button`
  appearance: none;
  border: none;
  background: ${ACCENT_COLOR};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  padding: 7px 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;

  span {
    display: inline-block;
    transition: transform 0.1s;
  }

  &:hover {
    background: #01507d;
  }

  &:active span {
    transform: scale(0.92);
  }

  &:disabled {
    background: #d1d5db;
    cursor: default;
  }
`;

const SolItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 6px 6px 18px;
  border-radius: 10px;
  background: ${(p) => (p.$dragging ? "#fff" : "#f5f5f6")};
  box-shadow: ${(p) => (p.$dragging ? "0 6px 20px rgba(0,0,0,0.28)" : "0 0 0 rgba(0,0,0,0)")};
  transform: rotate(${(p) => (p.$dragging ? `${p.$dragDir * 1}deg` : "0deg")});
  transform-origin: 95% center;
  cursor: default;
  overflow: hidden;
  user-select: none;
  transition: background 0.15s, box-shadow 0.2s, transform 0.2s;

  &:has(textarea:focus) {
    box-shadow: 0 0 4px 2px ${ACCENT_COLOR};
  }
`;

const SolSaveBtn = styled(SolAddBtn)`
  margin-left: auto;
`;

const SolDragHandle = styled.div`
  color: #d1d5db;
  font-size: 16px;
  cursor: grab;
  height: 33px;
  padding: 0 8px;
  margin-left: auto;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: #9ca3af;
  }

  &:active {
    cursor: grabbing;
  }
`;

const SolItemText = styled.div`
  font-size: 15px;
  color: #374151;
  line-height: 1.4;
  min-width: 0;
  align-self: center;
`;

const SolEditInput = styled.input`
  flex: 1;
  max-width: calc(100% - 106px);
  padding: 0;
  font-size: 15px;
  font-family: inherit;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  overflow: hidden;
  line-height: 1.4;
  field-sizing: content;
  align-self: center;
  min-width: 0;
`;

const SolActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-top: 3px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;

  ${SolItem}:not([data-dragging="true"]):hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;

const SolActionBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  color: ${(p) => (p.$danger ? "#d1d5db" : "#c4c4c4")};
  border-radius: 8px;
  transition: color 0.15s, background 0.15s;

  i {
    transition: transform 0.1s;
  }

  &:hover {
    color: ${(p) => (p.$danger ? "#ef4444" : ACCENT_COLOR)};
    background: ${(p) => (p.$danger ? "rgba(239, 68, 68, 0.08)" : "rgba(1, 96, 153, 0.08)")};
  }

  &:active {
    color: ${(p) => (p.$danger ? "#ef4444" : ACCENT_COLOR)};
    background: ${(p) => (p.$danger ? "rgba(239, 68, 68, 0.14)" : "rgba(1, 96, 153, 0.14)")};

    i {
      transform: scale(0.8);
    }
  }
`;
